import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useVideoPlaceholderStyles } from './styles';
import { COLORS } from '../../utils/colors';
import { DailyParticipant } from '@daily-co/daily-js';
import { initialize } from '../../utils/helpers';

const useAudioLevelFromTrack = (
  track: MediaStreamTrack | null | undefined,
  onLevelChange: (volume: number) => void,
  isVisibleRef: React.MutableRefObject<boolean>,
) => {
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!track || !isVisibleRef.current) return;

    const stream = new MediaStream([track]);
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    source.connect(analyser);

    const update = () => {
      if (!isVisibleRef.current) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        audioContext.close();
        return;
      }

      analyser.getByteTimeDomainData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const val = (dataArray[i] - 128) / 128;
        sum += val * val;
      }

      const rms = Math.sqrt(sum / dataArray.length);
      onLevelChange(rms);

      rafRef.current = requestAnimationFrame(update);
    };

    rafRef.current = requestAnimationFrame(update);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      source.disconnect();
      analyser.disconnect();
    };
  }, [track, onLevelChange, isVisibleRef]);
};

interface VideoPlaceholderProps {
  participant?: DailyParticipant | null | undefined;
  outerLayerSize: number;
  innerLayerSize: number;
  isVisible: boolean;
}

const VideoPlaceholder = ({
  participant,
  outerLayerSize,
  innerLayerSize,
  isVisible,
}: VideoPlaceholderProps) => {
  const styles = useVideoPlaceholderStyles();
  const isVisibleRef = useRef(isVisible);

  const [middleLayerSize, setMiddleLayerSize] = useState(0);
  const [innerRem, setInnerRem] = useState('0');
  const [middleRem, setMiddleRem] = useState('0');
  const [outerRem, setOuterRem] = useState('0');

  const [isOuterLayerVisible, setIsOuterLayerVisible] = useState(false);
  const [isMiddleLayerVisible, setIsMiddleLayerVisible] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');

  useEffect(() => {
    if (participant?.user_name) {
      setPlaceholderText(initialize(participant.user_name));
    }
  }, [participant?.user_name]);

  useEffect(() => {
    const middle = Math.round(
      (Number(outerLayerSize) + Number(innerLayerSize)) * 0.5,
    );
    setMiddleLayerSize(middle);
    setInnerRem(`${innerLayerSize / 16}rem`);
    setMiddleRem(`${middle / 16}rem`);
    setOuterRem(`${outerLayerSize / 16}rem`);
  }, [innerLayerSize, outerLayerSize]);

  useEffect(() => {
    console.log(`::::VideoPlaceholder isVisible ${placeholderText}`, isVisible);
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  const [audioTrack, setAudioTrack] = useState<MediaStreamTrack | null>(null);

  useEffect(() => {
    console.log('::::track changes...');
    if (participant?.tracks?.audio?.persistentTrack && isVisible) {
      setAudioTrack(participant.tracks.audio.persistentTrack);
    } else {
      setAudioTrack(null);
    }
  }, [participant?.tracks?.audio?.persistentTrack, isVisible]);

  const handleVolume = useCallback(
    (volume: number) => {
      const level = Math.round(volume * 1000);

      if (isVisibleRef.current) {
        setIsOuterLayerVisible(level > 30);
        setIsMiddleLayerVisible(level > 15);
      }
    },
    [placeholderText],
  );

  useAudioLevelFromTrack(audioTrack, handleVolume, isVisibleRef);

  return (
    <Box
      id={`_placeholder_${placeholderText}`}
      sx={{
        ...styles.localPlaceholder,
        opacity: isVisible ? 1 : 0,
      }}>
      <Box
        id={`_outer_rem_${outerRem}`}
        sx={{
          ...styles.wrapper,
          height: outerRem,
          width: outerRem,
          border: `.125rem solid ${
            isOuterLayerVisible ? `rgba(255, 110, 0, .3)` : 'transparent'
          }`,
          background: isOuterLayerVisible
            ? `rgba(255, 110, 0, 0.3)`
            : 'transparent',
        }}>
        <Box
          id={`_middle_rem_${middleRem}`}
          sx={{
            ...styles.wrapper,
            height: middleRem,
            width: middleRem,
            border: `.125rem solid ${
              isMiddleLayerVisible ? `rgba(255, 110, 0, .5)` : 'transparent'
            }`,
            background: isMiddleLayerVisible
              ? `rgba(255, 110, 0, 0.3)`
              : 'transparent',
          }}>
          <Box
            id={`_inner_rem_${innerRem}`}
            sx={{
              ...styles.wrapper,
              height: innerRem,
              width: innerRem,
              bgcolor: 'white',
            }}>
            <Typography
              color={COLORS.PRIMARY}
              fontWeight={600}
              fontSize={`${middleLayerSize / 48}rem`}>
              {placeholderText}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoPlaceholder;
